import { Icon } from '@iconify/react';
import RevealWrap from './RevealWrap';

const FEATURES = [
  {
    icon: 'solar:star-bold',
    title: '리뷰 작성이 이렇게 쉬울 수가',
    desc: '주문이 끝나면 자동으로 네이버 카카오 리뷰 작성 화면으로 연결됩니다. 고객이 귀찮아할 틈 없이, 자연스럽게 리뷰가 쌓입니다.',
  },
  {
    icon: 'solar:graph-up-bold',
    title: '리뷰가 쌓이면, 손님이 늘어납니다',
    desc: '네이버에서 맛집을 검색할 때 리뷰 수와 별점이 곧 매출입니다. 와우오더 도입 후 평균 리뷰 유입률이 300% 이상 증가합니다.',
  },
];

export default function NaverIntegration() {
  return (
    <section id="naver" className="py-28 lg:py-40 bg-neutral-950 text-white relative overflow-hidden">
      <div
        className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/4"
        style={{ background: 'radial-gradient(circle, rgba(3,199,90,0.08), transparent 70%)' }}
      />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-brand-400/4 rounded-full blur-3xl pointer-events-none translate-y-1/3" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <RevealWrap animation="left">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
              <span className="w-2 h-2 rounded-full bg-[#03C75A] animate-pulse" />
              <span className="text-neutral-400 text-sm font-medium">Naver / Kakao Review</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              그 어떤 업체보다<br />
              <span className="gradient-text">강력한 리뷰 시스템</span>
            </h2>
            <p className="text-neutral-400 text-lg leading-relaxed mb-10 max-w-lg">
              네이버 앱과 자동 연동되어, 고객이 QR 주문을 마치는 순간 리뷰 작성까지 자연스럽게 이어집니다. 사장님이 따로 부탁하지 않아도 리뷰가 쌓입니다.
            </p>

            <div className="space-y-6">
              {FEATURES.map((f, i) => (
                <RevealWrap key={f.title} className={`reveal-delay-${i + 1}`}>
                  <div className="group flex gap-4 p-5 -mx-5 rounded-2xl hover:bg-white/5 transition-colors duration-300">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-500"
                      style={{ background: 'rgba(3,199,90,0.15)' }}
                    >
                      <Icon icon={f.icon} className="text-[#03C75A] text-xl" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">{f.title}</h3>
                      <p className="text-neutral-400 leading-relaxed text-sm">{f.desc}</p>
                    </div>
                  </div>
                </RevealWrap>
              ))}
            </div>
          </RevealWrap>

          <RevealWrap animation="right" className="reveal-delay-1">
            <div className="bezel-card p-1">
              <div className="bezel-card-inner p-8 lg:p-10">
                <div className="bg-black/40 rounded-2xl p-6 border border-white/10 mb-6">
                  <div className="flex items-center gap-3 mb-5">
                    <img src="/landing/naver-icon.svg" alt="네이버" width={40} height={40} className="rounded-xl" />
                    <div>
                      <p className="text-white font-semibold text-sm">네이버 리뷰 현황</p>
                      <p className="text-neutral-500 text-xs">와우오더 도입 3개월 차</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-5">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">4.8</p>
                      <p className="text-neutral-500 text-xs">평균 별점</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#03C75A]">+324</p>
                      <p className="text-neutral-500 text-xs">신규 리뷰</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-brand-400">284%</p>
                      <p className="text-neutral-500 text-xs">방문율 증가</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {[
                      { stars: 5, text: 'QR로 주문하니까 너무 편하고 음식도 맛있어요!' },
                      { stars: 5, text: '리뷰 쓰기가 이렇게 간편한 건 처음이에요' },
                    ].map((r, i) => (
                      <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/5">
                        <div className="flex gap-0.5 mb-1">
                          {[...Array(r.stars)].map((_, j) => (
                            <Icon key={j} icon="solar:star-bold" className="text-amber-400 text-xs" />
                          ))}
                        </div>
                        <p className="text-neutral-300 text-xs">{r.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#03C75A' }}>
                    <span className="text-white font-bold text-sm">N</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-[#03C75A]/50 via-white/20 to-brand-400/50 relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                      <Icon icon="solar:link-bold" className="text-white text-xs" />
                    </div>
                  </div>
                  <img src="/landing/logo.png" alt="와우오더" width={40} height={40} className="rounded-xl" />
                </div>
              </div>
            </div>
          </RevealWrap>
        </div>
      </div>
    </section>
  );
}
